import {
    useState, useEffect, useRef, useCallback,
} from 'react';
import useAuthContext from 'contexts/AuthContext';
import { SensitivityAnalysisParameter, SensitivityAnalysisParams, SensitivityAnalysisResponse } from '../types';
import useDispatchSensitivityAnalysis from './useDispatchSensitivityAnalysis';
import useSensitivityTaskStatus from './useSensitivityTaskStatus';

interface SensitivityAnalysisParamsInput extends Omit<SensitivityAnalysisParams, 'parameters'> {
    parameters: SensitivityAnalysisParameter | undefined;
}

/**
 * Dispatches a sensitivity analysis Celery task, then polls for completion.
 * One params change or one retry dispatches exactly one task.
 */
export const useGetProFormaSensitivityAnalysis = (
    dealId: number,
    proFormaId: number,
    params: SensitivityAnalysisParamsInput,
    sourceId: number | null,
    options: { enabled: boolean } = { enabled: true },
) => {
    const { selectedOrganizationId } = useAuthContext();
    const [taskId, setTaskId] = useState<string | null>(null);
    const [data, setData] = useState<SensitivityAnalysisResponse | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [retryCount, setRetryCount] = useState(0);
    const dispatchIdRef = useRef(0);

    const dispatchMutation = useDispatchSensitivityAnalysis();

    const dispatch = useCallback((): Promise<{ taskId: string } | void> => {
        const { parameters } = params;
        if (!options.enabled || !parameters) return Promise.resolve();

        setIsLoading(true);
        setTaskId(null);
        setError(null);
        setData(undefined);

        const thisId = ++dispatchIdRef.current;
        const variables = {
            dealId, proFormaId, params: { ...params, parameters }, sourceId,
        };
        return dispatchMutation
            .mutateAsync(variables)
            .then((result) => {
                if (thisId === dispatchIdRef.current) {
                    setTaskId(result.taskId);
                }
                return result;
            })
            .catch((err) => {
                if (thisId === dispatchIdRef.current) {
                    setIsLoading(false);
                    setError(err);
                }
                throw err;
            });
    }, [options.enabled, dispatchMutation, dealId, proFormaId, params, sourceId]);    

    const paramsKey = JSON.stringify({
        dealId, proFormaId, params, sourceId, retryCount,
    });

    const prevParamsKeyRef = useRef<string>('');
    useEffect(() => {
        if (paramsKey !== prevParamsKeyRef.current && options.enabled && params.parameters) {
            prevParamsKeyRef.current = paramsKey;
            // dispatch sets the error itself, and only for the latest request; an older request's rejection is ignored here.
            dispatch().catch(() => {});
        }
    }, [paramsKey, options.enabled, params.parameters, dispatch]);

    const taskStatusQuery = useSensitivityTaskStatus({
        taskId,
        organizationId: selectedOrganizationId!,
        enabled: !!taskId && !!selectedOrganizationId && options.enabled,
    });

    useEffect(() => {
        if (taskStatusQuery.data?.status === 'SUCCESS' && taskStatusQuery.data.result) {
            setData(taskStatusQuery.data.result);
            setIsLoading(false);
            setTaskId(null);
        } else if (taskStatusQuery.data?.status === 'FAILURE') {
            setIsLoading(false);
            setTaskId(null);
            const message = taskStatusQuery.data.timedOut
                ? 'Sensitivity analysis timed out'
                : taskStatusQuery.data.message;
            setError(new Error(message));
        } else if (taskStatusQuery.isError) {  
            setIsLoading(false);  
            setTaskId(null);  
            const err = taskStatusQuery.error as unknown;  
            if (err instanceof Error) {  
                setError(err);  
            } else {  
                setError(new Error('Failed to fetch sensitivity analysis status', { cause: err }));
            }
        }
    }, [taskStatusQuery.data?.status, 
        taskStatusQuery.data?.result, 
        taskStatusQuery.data?.message, 
        taskStatusQuery.data?.timedOut, 
        taskStatusQuery.isError, 
        taskStatusQuery.error]);

    const isFetching = isLoading || (!!taskId && taskStatusQuery.isFetching);
    const retry = useCallback(() => setRetryCount(count => count + 1), []);

    return {
        data,
        isLoading,
        isFetching,
        error,
        retry,
    };
};

export default useGetProFormaSensitivityAnalysis;
